<template>
  <div class="h-full flex flex-col">
    <l-modal-header :title="title" />
    <l-modal-content>
      <div v-if="loading" class="flex-1 flex flex-col items-center justify-center gap-2 py-16">
        <l-icon name="status" class="animate-spin text-n-0" size="large" />
        <span class="text-n-200 text-sm">
          {{ t('companies-list.loading') }}
        </span>
      </div>
      <div v-else-if="fetchError" class="mt-8">
        <l-empty-case type="error">
          {{ t('companies-list.error-fetching-companies') }}
          <template #action>
            <l-button size="small" @click="updateCompanies()">
              {{ t('companies-list.retry') }}
            </l-button>
          </template>
        </l-empty-case>
      </div>
      <template v-else>
        <div class="flex flex-row items-center">
          <l-search-bar
            v-model="query"
            class="flex-1"
            size="medium"
            :debounce-time="500"
            @update:model-value="updateCompanies()"
          />
          <l-button
            icon="plus"
            class="ml-4"
            :disabled="!!validatingCompanyId"
            @click="createCompany()"
          >
            {{ t('companies-list.new-company') }}
          </l-button>
        </div>
        <transition-group name="companies-list">
          <div
            v-for="company in companies"
            :key="company.id"
            class="relative flex flex-row justify-between bg-n-700 rounded p-4 my-4"
            :class="validatingCompanyId ? 'cursor-not-allowed' : 'cursor-pointer'"
            @click="!validatingCompanyId && selectCompany(company)"
          >
            <div>
              <div class="font-heading text-base">{{ company.name }}</div>
              <div class="font-body text-sm font-normal text-n-200">
                {{ company.taxId }}
              </div>
            </div>
            <div
              v-if="validatingCompanyId !== company.id"
              class="text-v-300 pl-6"
              @click.stop="!validatingCompanyId && editCompany(company)"
            >
              <l-icon name="edit" />
            </div>
            <div
              v-if="validatingCompanyId === company.id"
              class="absolute inset-0 flex items-center justify-center gap-2 bg-n-800/50 rounded"
            >
              <l-icon name="status" class="animate-spin text-n-0" size="large" />
              <span class="text-n-0 text-sm">
                {{ t('companies-list.validating') }}
              </span>
            </div>
          </div>
        </transition-group>
        <div v-if="companies.length === 0" class="mt-8">
          <l-empty-case type="empty">
            {{ t('companies-list.empty') }}
          </l-empty-case>
        </div>
      </template>
    </l-modal-content>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  LButton,
  LEmptyCase,
  LIcon,
  LModalContent,
  LModalHeader,
  LSearchBar
} from '@last/core-ui/paprika'

import { legalEntitiesApi } from '@/api/client'
import { useConfigStore } from '@/store/config'
import { type Company } from '@/types'

defineProps<{
  title: string
}>()

const configStore = useConfigStore()

const { t } = useI18n()

const emit = defineEmits<{
  modify: [Company | undefined]
  select: [Company]
}>()

const companies = ref<Company[]>([])
const query = ref('')
const loading = ref(false)
const fetchError = ref(false)
const validatingCompanyId = ref<string | null>(null)
let abortController: AbortController | null = null

onMounted(() => {
  updateCompanies()
})

onBeforeUnmount(() => {
  abortController?.abort()
})

async function updateCompanies() {
  loading.value = true
  fetchError.value = false
  try {
    companies.value = await legalEntitiesApi.searchCompanies({ query: query.value })
  } catch {
    fetchError.value = true
  } finally {
    loading.value = false
  }
}

async function selectCompany(company: Company) {
  abortController?.abort()
  abortController = new AbortController()
  const { signal } = abortController

  validatingCompanyId.value = company.id

  try {
    const result = await legalEntitiesApi.validateLegalEntity({
      validateLegalEntityRequest: {
        taxId: company.taxId,
        companyName: company.name,
        countryCode: configStore.config.locationCountryCode
      }
    })

    if (signal.aborted) return

    if (!result.isValid) {
      validatingCompanyId.value = null
      emit('modify', company)
      return
    }
  } catch {
    if (signal.aborted) return

    validatingCompanyId.value = null
    emit('modify', company)
    return
  }

  validatingCompanyId.value = null
  emit('select', company)
}

function createCompany() {
  abortController?.abort()
  validatingCompanyId.value = null
  emit('modify', undefined)
}

function editCompany(company: Company) {
  abortController?.abort()
  validatingCompanyId.value = null
  emit('modify', company)
}

</script>

<style scoped>
.companies-list-enter-from {
  opacity: 0;
  transform: scale(0.6);
}

.companies-list-enter-active {
  transition: all 0.3s ease;
}

.companies-list-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

.companies-list-leave-active {
  transition: all 0.3s ease;
  position: absolute;
}

.companies-list-move {
  transition: all 0.2s ease;
}
</style>
